// Create a new file: src/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { checkSpecialtyAccess, normalizeSpecialtyName } from './constants';
import { updateDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userCountry, setUserCountry] = useState("India");
  const [loading, setLoading] = useState(true);
  const [dataVersion, setDataVersion] = useState(0); // Force refresh mechanism
  const [userPaidSpecialties, setUserPaidSpecialties] = useState([]);
  const [userData, setUserData] = useState(null);


  // Force refresh user data
const refreshUserData = async (forceRefresh = false) => {
  if (!auth.currentUser) return;

  try {
    console.log('Refreshing user data...', { forceRefresh, userId: auth.currentUser.uid });
    
    // Always fetch fresh data, don't rely on cache
    const [userSnap, profileSnap] = await Promise.all([
      getDoc(doc(db, 'users', auth.currentUser.uid)),
      getDoc(doc(db, 'profiles', auth.currentUser.uid))
    ]);

    let userCountryData = "India";
    let profileData = null;
    let userDataFromDb = null;
    let specialtyAccess = [];

    // Get user data including payment status and specialty access
    if (userSnap.exists()) {
      userDataFromDb = userSnap.data();
      
      
      // New per-specialty access
      specialtyAccess = userDataFromDb.paidSpecialties || [];
      
      console.log('User specialty access:', specialtyAccess);
    }

    // Get profile data including country
    if (profileSnap.exists()) {
      profileData = profileSnap.data();
      userCountryData = profileData.country || "India";
      console.log('User country from profile:', userCountryData);
    }

    // Update all states
    setUserData(userDataFromDb);
    setUserPaidSpecialties(specialtyAccess);
    setUserCountry(userCountryData);
    setUserProfile(profileData);
    
    // Increment version to trigger re-renders
    setDataVersion(prev => prev + 1);
    
    console.log('User data refreshed:', {
      specialtyCount: specialtyAccess.length,
      country: userCountryData,
      hasProfile: !!profileData,
      version: dataVersion + 1
    });
    
  } catch (error) {
    console.error("Error refreshing user data:", error);
  }
};
// Check if user has paid for any specialty
const hasAnySpecialtyAccess = () => {
  return userPaidSpecialties && userPaidSpecialties.length > 0;
};
// Check if user has access to a specific specialty
const hasSpecialtyAccess = (specialty) => {
  return checkSpecialtyAccess(userPaidSpecialties, specialty);
};

// Function to add specialty access after payment
const addSpecialtyAccess = async (specialty, paymentData) => {
  try {
    if (!auth.currentUser) throw new Error("User not authenticated");
    
    const userRef = doc(db, "users", auth.currentUser.uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const currentData = userSnap.data();
      const currentPaidSpecialties = currentData.paidSpecialties || [];
      
      // Check if already purchased
      if (checkSpecialtyAccess(currentPaidSpecialties, specialty)) {
        throw new Error("Specialty already purchased");
      }
      
      const newSpecialtyAccess = {
        specialty: specialty,
        specialtyNormalized: normalizeSpecialtyName(specialty),
        paidAt: new Date().toISOString(),
        paymentId: paymentData.paymentId,
        amount: paymentData.amount,
        currency: paymentData.currency,
        country: paymentData.country
      };
      
      const updatedSpecialties = [...currentPaidSpecialties, newSpecialtyAccess];
      
      await updateDoc(userRef, {
        paidSpecialties: updatedSpecialties,
        lastPurchase: new Date().toISOString()
      });
      
      // Update local state
      setUserPaidSpecialties(updatedSpecialties);
      setDataVersion(prev => prev + 1);
      
      // Trigger storage event for other tabs
      localStorage.setItem('user_data_updated', Date.now().toString());
      localStorage.removeItem('user_data_updated');
      
      return true;
    }
    
    throw new Error("User document not found");
  } catch (error) {
    console.error("Error adding specialty access:", error);
    throw error;
  }
};

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('Auth state changed:', { 
        hasUser: !!firebaseUser, 
        userId: firebaseUser?.uid 
      });
      
      setUser(firebaseUser);
      
      if (firebaseUser) {
        // User is authenticated, fetch their data
        await refreshUserData(true);
      } else {
  // User is not authenticated, reset state
  setUserData(null);
  setUserProfile(null);
  setUserPaidSpecialties([]);
  setUserCountry("India");
  setDataVersion(0);
}
      
      setLoading(false);
    });

    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for page visibility changes to refresh data
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        console.log('Page became visible, refreshing user data...');
        refreshUserData(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for storage events (in case user updates data in another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user_data_updated' && auth.currentUser) {
        console.log('Detected user data update from another tab');
        refreshUserData(true);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

const value = {
  user,
  userData,
  userProfile,
  userCountry,
  userPaidSpecialties,
  loading,
  dataVersion,
  refreshUserData,
  hasSpecialtyAccess,
  hasAnySpecialtyAccess,
  addSpecialtyAccess,
  
  // Helper methods
  updateUserProfile: (profile) => {
    setUserProfile(profile);
    if (profile.country) {
      setUserCountry(profile.country);
    }
    // Trigger storage event for other tabs
    localStorage.setItem('user_data_updated', Date.now().toString());
    localStorage.removeItem('user_data_updated');
  }
};

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};